import axios from 'axios';
import { AuthRequest, AuthResponse } from '../types/auth';
import { PlayData, PlayResponse, PlaysListData, InstantWinResponse } from '../types/plays';
import { ContestData } from '../types/contest';

export interface PlaysData {
  play: any[];
}

// Callback per gestire errori di autenticazione
let authErrorCallback: ((errorData?: { title: string; message: string }) => void) | null = null;

// Funzione per registrare il callback di errore
export const setAuthErrorCallback = (callback: ((errorData?: { title: string; message: string }) => void) | null) => {
  authErrorCallback = callback;
};

// Funzione per creare un interceptor per le chiamate autenticate
const createAuthInterceptor = () => {
  return (error: any) => {
    console.error('Authenticated API Error:', error);
    
    // Gestione errori 401 - Token non valido
    if (error.response?.status === 401) {
      console.log('Token non valido in chiamata autenticata, triggerando callback di errore');
      if (authErrorCallback) {
        authErrorCallback({
          title: 'Errore nella sessione',
          message: 'Effettua nuovamente il login per continuare.'
        });
      }
      throw new Error('Token non valido. Effettua nuovamente il login.');
    }
    
    if (error.code === 'ECONNABORTED') {
      throw new Error('Timeout della richiesta. Riprova.');
    }
    if (error.response?.status === 0) {
      throw new Error('Errore di connessione. Verifica la connessione internet.');
    }
    return Promise.reject(error);
  };
};

// API restituisce elapsed in microsecondi; arrotondiamo in secondi per il frontend
const elapsedMicrosToSeconds = (micros: number | undefined): number =>
  Math.round((micros ?? 0) / 1_000_000);

// URL delle API (stesso per dev e prod)
const API_BASE_URL = 'https://friends.davidebettio.com/api/v1';
// const API_BASE_URL = 'https://friends-api.adattivo-cloud.com/api/v1';
// const API_BASE_URL = 'https://friends-api.mcdonalds.it/api/v1';

// Funzione per ottenere gli headers comuni
const getCommonHeaders = () => {
  const headers: any = {
    'Content-Type': 'application/json',
  };
  
  const testDateFormatted = localStorage.getItem('date');
  if (testDateFormatted) {
    headers['x-mcdx-test-date'] = testDateFormatted;
  }
  
  return headers;
};

const authApi = axios.create({
  baseURL: API_BASE_URL,
  headers: getCommonHeaders(),
  // Aggiungi timeout e gestione errori
  timeout: 10000,
});

// Interceptor per gestire gli errori
authApi.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    
    // Gestione errori 401 - Token non valido
    if (error.response?.status === 401) {
      console.log('Token non valido, triggerando callback di errore');
      if (authErrorCallback) {
        authErrorCallback({
          title: 'Sessione Scaduta',
          message: 'La tua sessione è scaduta o il token non è più valido. Per favore, effettua nuovamente il login per continuare.'
        });
      }
      throw new Error('Token non valido. Effettua nuovamente il login.');
    }
    
    // Gestione errori generici
    if (error.code === "ERR_BAD_REQUEST") {
      if (authErrorCallback) {
        authErrorCallback({
          title: 'Errore di Connessione',
          message: 'Qualcosa è andato storto. Per favore, effettua nuovamente il login.'
        });
      }
      throw new Error('Qualcosa è andato storto. Effettua nuovamente il login.');
    }
    
    if (error.code === 'ECONNABORTED') {
      throw new Error('Timeout della richiesta. Riprova.');
    }
    if (error.response?.status === 0) {
      throw new Error('Errore di connessione. Verifica la connessione internet.');
    }
    return Promise.reject(error);
  }
);

export const authService = {
  // Registra un nuovo utente
  register: async (userData: AuthRequest): Promise<AuthResponse> => {
    try {
      console.log('Invio dati a:', `${API_BASE_URL}/user/auth/`);
      console.log('Headers:', getCommonHeaders());
      console.log('Dati inviati:', userData);
      
      const response = await authApi.post('/user/auth/', userData);
      console.log('Risposta ricevuta:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Errore durante la registrazione:', error);
      
      if (error.response?.status === 404) {
        throw new Error('Endpoint API non trovato. Verifica l\'URL dell\'API.');
      }
      if (error.response?.status === 500) {
        throw new Error('Errore del server. Riprova più tardi.');
      }
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      
      throw new Error(error.message || 'Errore durante la registrazione');
    }
  },

  // Salva il JWT nel localStorage
  saveToken: (token: string): void => {
    localStorage.setItem('jwt_token', token);
  },

  // Ottieni il JWT dal localStorage
  getToken: (): string | null => {
    return localStorage.getItem('jwt_token');
  },

  // Rimuovi il JWT dal localStorage
  logOut: (): void => {
    localStorage.removeItem('jwt_token');
    window.location.reload();
  },

  // Verifica se l'utente è autenticato
  isAuthenticated: (): boolean => {
    const token = localStorage.getItem('jwt_token');
    return !!token;
  },

  // Nuova funzione per decodificare il JWT
  decodeJWT: (token: string): any => {
    try {
      // Il JWT ha 3 parti separate da punti: header.payload.signature
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Errore nel decodificare JWT:', error);
      return null;
    }
  },

  // Funzione per ottenere i dati utente dal JWT
  getUserFromJWT: (): any => {
    const token = localStorage.getItem('jwt_token');
    if (!token) return null;
    
    return authService.decodeJWT(token);
  },

  // Nuova funzione per chiamate autenticate
  getAuthenticatedRequest: () => {
    const token = localStorage.getItem('jwt_token');
    const headers = getCommonHeaders();
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const authApi = axios.create({
      baseURL: API_BASE_URL,
      headers,
      timeout: 10000,
    });

    // Aggiungi l'interceptor per gestire gli errori 401
    authApi.interceptors.response.use(
      (response) => response,
      createAuthInterceptor()
    );

    return authApi;
  },

  // Funzione per ottenere i contest
  getContest: async (): Promise<ContestData> => {
    try {
      const authApi = authService.getAuthenticatedRequest();
      console.log('Chiamata autenticata a:', `${API_BASE_URL}/user/contests/`);
      console.log('Headers:', getCommonHeaders());
      
      const response = await authApi.get('/user/contests/');
      console.log('Risposta contest:', response.data);
      
      return response.data;
    } catch (error: any) {
      console.error('Errore durante il recupero contest:', error);
      
      // Se c'è un response dal server, restituisci i dati originali
      if (error.response?.data) {
        console.log('Response data dal server:', error.response.data);
        return error.response.data;
      }
      
      // Altrimenti gestisci gli errori generici
      if (error.response?.status === 404) {
        throw new Error('Endpoint contest non trovato.');
      }
      if (error.response?.status === 500) {
        throw new Error('Errore del server. Riprova più tardi.');
      }
      
      throw new Error(error.message || 'Errore durante il recupero contest');
    }
  },

  // Funzione per fare check-in al contest
  checkin: async (contestId: string): Promise<any> => {
    try {
      const authApi = authService.getAuthenticatedRequest();
      console.log('Chiamata check-in a:', `${API_BASE_URL}/user/contests/checkin`);
      
      const response = await authApi.post('/user/contests/checkin', {
        contestId
      });
      
      console.log('Risposta check-in:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Errore durante il check-in:', error);
      
      // Se c'è un response dal server, restituisci i dati
      if (error.response?.data) {
        console.log('Response data dal server:', error.response.data);
        return error.response.data;
      }
      
      throw new Error(error.message || 'Errore durante il check-in');
    }
  },

  postPlay: async (playData: PlayData): Promise<PlayResponse | ContestData> => {
    try {
      const authApi = authService.getAuthenticatedRequest();
      const response = await authApi.post('/user/contests/', playData);
      console.log('Response post play:', response.data);
      const data = response.data as PlayResponse;
      return { ...data, elapsed: elapsedMicrosToSeconds(data.elapsed) };
    } catch (error: any) {
      console.error('Errore durante il post play:', error);
      // Se c'è un response dal server, restituisci i dati originali
      if (error.response?.data) {
        return error.response.data as ContestData;
      }
      return error.response.data.code;
    }
  },

  // Instant Win: chiamata a /user/instantwin/ con contestId
  getInstantWin: async (contestId: string): Promise<InstantWinResponse> => {
    try {
      const authApi = authService.getAuthenticatedRequest();
      const response = await authApi.post('/user/instantwin/', {
        contestId
      });
      return response.data as InstantWinResponse;
    } catch (error: any) {
      console.error('Errore durante Instant Win:', error);
      if (error.response?.data) {
        throw new Error(error.response.data?.message || 'Errore Instant Win');
      }
      throw new Error(error.message || 'Errore durante Instant Win');
    }
  },

  // Funzione per ottenere i dati dei votes
  getPlays: async (): Promise<PlaysListData> => {
    try {
      const authApi = authService.getAuthenticatedRequest();
      console.log('Chiamata autenticata a:', `${API_BASE_URL}/user/plays/`);
      console.log('Headers:', getCommonHeaders());
      
      const response = await authApi.get('/user/plays/');
      const data = response.data as PlaysListData;
      return {
        plays: data.plays.map((p) => ({
          ...p,
          elapsed: elapsedMicrosToSeconds(p.elapsed),
        })),
      };
    } catch (error: any) {
      console.error('Errore durante il recupero plays:', error);
      
      if (error.response?.status === 404) {
        throw new Error('Endpoint plays non trovato.');
      }
      if (error.response?.status === 500) {
        throw new Error('Errore del server. Riprova più tardi.');
      }
      
      throw new Error(error.message || 'Errore durante il recupero plays');
    }
  },  

};

export default authService; 